import React, { Suspense } from 'react';
import {
  HashRouter,
  BrowserRouter,
  Route,
  Routes,
  Navigate,
  useParams,
} from 'react-router-dom';
import { LayoutGroup } from 'framer-motion';
import AuthenticatedRoute from './AuthenticatedRoute';

import { Library } from '../features/library';
import DefaultRoute from '../features/app/DefaultRoute';
import Signout from '../features/auth/Signout';
import LoaderFullScreen from '../features/app/loader/LoaderFullScreen';
import { MultiFileChatPage } from '../features/chat/multi-file';
import Dashboard from '../features/dashboard';

import * as LazyComponents from './LazyComponents';

const TeamRedirect = () => {
  const { teamId } = useParams();
  return <Navigate to={`/library/${teamId}/recents`} replace />;
};

/**
 * Hybrid Routing Strategy:
 * - Video sharing: Clean URLs (/v/{shareId}) with BrowserRouter
 * - Templates: Hash-based URLs (/#/shared-template/{shareId}) with HashRouter
 * - Legacy redirect: /#/shared/{shareId} → /v/{shareId}
 *
 * TODO
 * /recover
 * recording request: /recording/for/:id
 * recording request expired: /expired-request
 * emailverify
 * emailroute
 *
 */

const AppRoutes = () => {
  // Get the current pathname and determine base path
  const currentPath = window.location.pathname;

  // Check if this is a video/folder sharing URL path (clean URLs)
  // Handle both /v/shareId and /app/v/shareId patterns, /f/shareId patterns
  const pathSegments = currentPath.split('/').filter(Boolean);
  const vIndex = pathSegments.indexOf('v');
  const fIndex = pathSegments.indexOf('f');
  const isVideoSharedPath = vIndex !== -1 && pathSegments[vIndex + 1]; // Ensure there's a shareId
  const isFolderSharedPath = fIndex !== -1 && pathSegments[fIndex + 1]; // Ensure there's a shareId

  // Check if this is a legacy hash-based video shared URL
  // ONLY match old format /#/shared/{shareId}, NOT new format /#/shared/{teamId}/f/... or /#/shared/{teamId}/u/...
  const { hash } = window.location;
  const hashPath = hash.substring(1); // Remove the # symbol
  const hashSegments = hashPath.split('/').filter(Boolean);

  // Legacy format: /shared/{shareId} (exactly 2 segments: 'shared' and shareId)
  // New formats have more segments: /shared/{teamId}/f/{folderId}/{fileId} or /shared/{teamId}/u/{userId}/{fileId}
  const isLegacyHashVideoSharedPath =
    hashSegments.length === 2 &&
    hashSegments[0] === 'shared' &&
    hashSegments[1]; // Has a shareId

  // Handle legacy hash-based video shared URLs by redirecting to clean URLs
  if (isLegacyHashVideoSharedPath && !isVideoSharedPath) {
    const shareId = hashSegments[1];
    const searchParams = window.location.search;

    // Determine the correct base path for the redirect
    const basePath = currentPath.replace(/\/$/, ''); // Remove trailing slash
    const newUrl = basePath.includes('/app')
      ? `/app/v/${shareId}${searchParams}`
      : `/v/${shareId}${searchParams}`;

    window.location.replace(newUrl);
    return null;
  }

  if (isVideoSharedPath) {
    // Determine the base path for BrowserRouter
    const baseName =
      vIndex > 0 ? `/${pathSegments.slice(0, vIndex).join('/')}` : undefined;

    // Use BrowserRouter for clean shared URLs
    return (
      <BrowserRouter basename={baseName}>
        <Routes>
          <Route
            path="/v/:shareId"
            element={
              <Suspense fallback={<LoaderFullScreen />}>
                <LazyComponents.SharedVideo />
              </Suspense>
            }
          />
        </Routes>
      </BrowserRouter>
    );
  }

  if (isFolderSharedPath) {
    // Determine the base path for BrowserRouter
    const baseName =
      fIndex > 0 ? `/${pathSegments.slice(0, fIndex).join('/')}` : undefined;

    // Use BrowserRouter for clean shared folder URLs
    return (
      <BrowserRouter basename={baseName}>
        <Routes>
          <Route
            path="/f/:shareId"
            element={
              <Suspense fallback={<LoaderFullScreen />}>
                <LazyComponents.SharedFolder />
              </Suspense>
            }
          />
          <Route
            path="/f/:shareId/:fileId"
            element={
              <Suspense fallback={<LoaderFullScreen />}>
                <LazyComponents.SharedVideo />
              </Suspense>
            }
          />
        </Routes>
      </BrowserRouter>
    );
  }

  // Use HashRouter for all authenticated routes
  return (
    <HashRouter>
      <LayoutGroup>
        <Routes>
          {/* All authenticated routes will render after fetching initial data */}
          <Route element={<AuthenticatedRoute />}>
            <Route index element={<DefaultRoute />} />
            <Route path="library">
              <Route index element={<Library />} />
              <Route path=":teamId" element={<TeamRedirect />} />
              <Route path=":teamId/:folderId" element={<Library />} />
              <Route
                path=":teamId/:folderId/:fileId"
                element={
                  <Suspense fallback={null}>
                    <LazyComponents.SelectedVideo />
                  </Suspense>
                }
              />
            </Route>
            <Route path="live">
              <Route
                index
                element={
                  <Suspense fallback={<LoaderFullScreen />}>
                    <LazyComponents.Live />
                  </Suspense>
                }
              />
              <Route
                path=":teamId/:folderId"
                element={
                  <Suspense fallback={<LoaderFullScreen />}>
                    <LazyComponents.Live />
                  </Suspense>
                }
              />
              <Route
                path=":teamId/:folderId/:fileId"
                element={
                  <Suspense fallback={<LoaderFullScreen />}>
                    <LazyComponents.Live />
                  </Suspense>
                }
              />
            </Route>
            <Route
              path="search"
              element={
                <Suspense fallback={<LoaderFullScreen />}>
                  <LazyComponents.SearchPage />
                </Suspense>
              }
            />
            <Route
              path="shared"
              element={
                <Suspense fallback={<LoaderFullScreen />}>
                  <LazyComponents.Shared />
                </Suspense>
              }
            />
            <Route
              path="shared/:teamId/f/:folderId/:fileId"
              element={
                <Suspense fallback={<LoaderFullScreen />}>
                  <LazyComponents.SharedFileFromFolder />
                </Suspense>
              }
            />
            <Route
              path="shared/:teamId/u/:userId/:fileId"
              element={
                <Suspense fallback={<LoaderFullScreen />}>
                  <LazyComponents.SharedFileFromUser />
                </Suspense>
              }
            />
            <Route
              path="accept-invitation"
              element={
                <Suspense fallback={<LoaderFullScreen />}>
                  <LazyComponents.AcceptInvitation />
                </Suspense>
              }
            />
            <Route
              path="clone-share"
              element={
                <Suspense fallback={<LoaderFullScreen />}>
                  <LazyComponents.CloneShare />
                </Suspense>
              }
            />
            <Route
              path="events"
              element={
                <Suspense fallback={<LoaderFullScreen />}>
                  <LazyComponents.EventsPage />
                </Suspense>
              }
            />
            <Route
              path="multi-ask-ai"
              element={
                <Dashboard>
                  <MultiFileChatPage />
                </Dashboard>
              }
            />
            <Route
              path="history"
              element={
                <Suspense fallback={<LoaderFullScreen />}>
                  <LazyComponents.HistoryPage />
                </Suspense>
              }
            />
            <Route
              path="settings/:settingKey"
              element={
                <Suspense fallback={<LoaderFullScreen />}>
                  <LazyComponents.Settings />
                </Suspense>
              }
            />
            <Route path="dashboard" element={<DefaultRoute />} />
          </Route>
          <Route
            path="/signup"
            element={
              <Suspense fallback={<LoaderFullScreen />}>
                <LazyComponents.SignUp />
              </Suspense>
            }
          />
          <Route
            path="/signin"
            element={
              <Suspense fallback={<LoaderFullScreen />}>
                <LazyComponents.SignIn />
              </Suspense>
            }
          />
          <Route path="/signout" element={<Signout />} />
          <Route
            path="/password/reset"
            element={
              <Suspense fallback={<LoaderFullScreen />}>
                <LazyComponents.ResetPassword />
              </Suspense>
            }
          />
          <Route
            path="/password/forgot"
            element={
              <Suspense fallback={<LoaderFullScreen />}>
                <LazyComponents.ForgotPassword />
              </Suspense>
            }
          />
          <Route
            path="/oauth/authorize"
            element={
              <Suspense fallback={<LoaderFullScreen />}>
                <LazyComponents.OAuthAuthorize />
              </Suspense>
            }
          />
          <Route path="/error" element={<LazyComponents.Error />} />
          <Route path="/loader" element={<LoaderFullScreen />} />
          <Route
            path="/recorder"
            element={
              <Suspense fallback={<LoaderFullScreen />}>
                <LazyComponents.RecorderDemo />
              </Suspense>
            }
          />
          <Route
            path="/contact-sales"
            element={
              <Suspense fallback={<LoaderFullScreen />}>
                <LazyComponents.ContactSalesPage />
              </Suspense>
            }
          />
          <Route
            path="/contact-support"
            element={
              <Suspense fallback={<LoaderFullScreen />}>
                <LazyComponents.ContactSupportPage />
              </Suspense>
            }
          />
          {/* Public tools and shared templates */}
          <Route
            path="/shared-template/:shareId"
            element={
              <Suspense fallback={<LoaderFullScreen />}>
                <LazyComponents.SharedTemplate />
              </Suspense>
            }
          />
          <Route
            path="/tool"
            element={
              <Suspense fallback={<LoaderFullScreen />}>
                <LazyComponents.MicroTool />
              </Suspense>
            }
          />
          <Route
            path="/tool/:toolName"
            element={
              <Suspense fallback={<LoaderFullScreen />}>
                <LazyComponents.MicroTool />
              </Suspense>
            }
          />
          <Route
            path="/editor"
            element={
              <Suspense fallback={<LoaderFullScreen />}>
                <LazyComponents.EditorPreview />
              </Suspense>
            }
          />
          <Route
            path="/emailroute"
            element={
              <Suspense fallback={<LoaderFullScreen />}>
                <LazyComponents.Redirect />
              </Suspense>
            }
          />
          <Route
            path="*"
            element={
              <Suspense fallback={<LoaderFullScreen />}>
                <LazyComponents.Error />
              </Suspense>
            }
          />
        </Routes>
      </LayoutGroup>
    </HashRouter>
  );
};

export default AppRoutes;
